// packages/protocol/src/schemas/MotorCalibration.ts
//
// Plan 22 — Motor calibration & state.
//
// CalibrationEvent docs live at:
//   workspaces/{wid}/projects/{pid}/motors/{mid}/calibration_events/{eid}
//
// They are append-only — the UI never edits or deletes them. The motor doc's
// `current_position`, `last_calibrated_ms`, and `soft_limit_*` fields are
// patched atomically with each event write so the read state stays in sync
// with the most recent calibration record.
//
// `RecordCalibrationSchema` is the discriminated union accepted by
// `POST /motors/:mid/calibration` — one variant per kind.

import { z } from 'zod';

export const CalibrationEventKindSchema = z.enum([
  'mark_home',
  'set_position',
  'soft_limit_update',
  'note',
]);

export const CalibrationEventSchema = z.object({
  id: z.string().min(1),
  motor_id: z.string().min(1),
  kind: CalibrationEventKindSchema,
  // Step position after the event. null for note/limit events that don't
  // change position.
  position_after: z.number().int().nullable(),
  soft_limit_min: z.number().int().nullable(),
  soft_limit_max: z.number().int().nullable(),
  user_id: z.string().min(1),
  user_email: z.string().nullable(),
  note: z.string().nullable(),
  created_at_ms: z.number().int().nonnegative(),
});

// Discriminated request body for POST /motors/:mid/calibration.
export const RecordCalibrationSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('mark_home'),
    note: z.string().nullable().optional(),
  }),
  z.object({
    kind: z.literal('set_position'),
    position: z.number().int(),
    note: z.string().nullable().optional(),
  }),
  z.object({
    kind: z.literal('soft_limit_update'),
    min: z.number().int().nullable(),
    max: z.number().int().nullable(),
    note: z.string().nullable().optional(),
  }),
  z.object({
    kind: z.literal('note'),
    note: z.string().min(1),
  }),
]);

export type ParsedCalibrationEvent = z.infer<typeof CalibrationEventSchema>;
export type ParsedCalibrationEventKind = z.infer<typeof CalibrationEventKindSchema>;
export type ParsedRecordCalibration = z.infer<typeof RecordCalibrationSchema>;
